import {
  differenceInDays,
  format,
  formatISO,
  formatRelative,
  getDay,
  getMonth,
  getYear,
  subDays,
} from 'date-fns';
import { type Locale, nb, sv } from 'date-fns/locale';
import type { MarketCode } from '../@types/Token';
import useMarketCode from '../data/branch/useMarketCode';

const resolveLocale = (marketCode?: MarketCode) => {
  switch (marketCode) {
    case 'NO':
      return nb;
    //case 'SE':
    default:
      return sv;
  }
};

export const formatRelativeSv = (date: string | Date, marketCode?: MarketCode) =>
  formatRelative(new Date(date), new Date(), { locale: resolveLocale(marketCode) });

const numToDay = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'];
const numToMonth = [
  'Januari',
  'Februari',
  'Mars',
  'April',
  'Maj',
  'Juni',
  'Juli',
  'Augusti',
  'September',
  'Oktober',
  'November',
  'December',
];

export const GetTimePresentation = (d: string) => {
  const currentDate = new Date();
  const currentDateYear = getYear(currentDate);
  const currentWeekDay = getDay(currentDate);
  const previousDate = subDays(new Date(), 1);
  const previousWeekDay = getDay(previousDate);
  const date = new Date(d);
  const dateWeekDay = getDay(date);
  const dateMonth = getMonth(date);
  const dateYear = getYear(date);

  const diffInDays = differenceInDays(currentDate, date);
  if (diffInDays < 6) {
    if (currentWeekDay === dateWeekDay) {
      return `Idag ${format(date, 'HH:mm')}`;
    } else if (previousWeekDay === dateWeekDay) {
      return `Igår ${format(date, 'HH:mm')}`;
    }
    return `${numToDay[dateWeekDay]} ${format(date, 'HH:mm')}`;
  }
  return `${format(date, 'd')} ${numToMonth[dateMonth]} ${
    currentDateYear > dateYear ? dateYear : ''
  }`;
};

export const formatYYYYMMDD = (d?: string | Date) =>
  d ? format(new Date(d), 'yyyy-MM-dd') : undefined;

export const formatYYYYMMDDHHmm = (d?: string | Date) =>
  d ? format(new Date(d), 'yyyy-MM-dd HH:mm') : undefined;

export const useDateLocale = () => {
  const [marketCode] = useMarketCode();
  return resolveLocale(marketCode);
};

export const formatYYYYMMDDLocale = (d: string | undefined | Date, locale: Locale) =>
  d ? format(new Date(d), 'yyyy-MM-dd', { locale }) : undefined;

const time = 'HH:mm';
interface TableDateOptions {
  locale: Locale;
  time?: boolean;
}

export const formatTableDate = (d: string | undefined | Date, options: TableDateOptions) => {
  if (!d) return undefined;
  const date = new Date(d);

  if (time) {
    return `${format(date, 'yyyy-MM-dd', { locale: options.locale })} (${format(date, time, { locale: options.locale })})`;
  }

  return format(date, 'yyyy-MM-dd', { locale: options.locale });
};

export const toRFC3339 = (date: Date) => formatISO(date);

/**
 * Format a date range with smart formatting:
 * - If no end date: "22 oktober 10:00" or "22 oktober 2024 10:00"
 * - If same day: "22 oktober 10:00 - 12:00" or "22 oktober 2024 10:00 - 12:00"
 * - If different days: "22 oktober 10:00 - 23 oktober 08:00" or "22 oktober 2024 10:00 - 23 oktober 2024 08:00"
 */
export const formatDateRange = (
  start: Date,
  end?: Date | null,
  locale?: Locale,
  options?: { includeYear?: boolean },
): string => {
  const dateFormat = options?.includeYear ? 'd MMMM yyyy HH:mm' : 'd MMMM HH:mm';

  if (!end) {
    return format(start, dateFormat, { locale });
  }

  const isSameDay = start.toDateString() === end.toDateString();

  if (isSameDay) {
    return `${format(start, dateFormat, { locale })} - ${format(end, 'HH:mm', { locale })}`;
  }

  return `${format(start, dateFormat, { locale })} - ${format(end, dateFormat, { locale })}`;
};
